import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DateTimeDisplay from './DateTimeDisplay';
import { API_ENDPOINTS } from '../config/api';

const StaffDashboard = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [feedbackRecords, setFeedbackRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [assignedAdminCards, setAssignedAdminCards] = useState([]);
  const [availableAdminCards, setAvailableAdminCards] = useState([]);

  useEffect(() => {
    checkStaffAuth();
  }, []);

  useEffect(() => {
    if (staff) {
      loadStaffData();
      loadAdminCards();
    }
  }, [staff]);

  const checkStaffAuth = () => {
    const staffUser = localStorage.getItem('staffUser');
    if (!staffUser) {
      navigate('/staff-login');
      return;
    }

    const staffData = JSON.parse(staffUser);
    if (staffData.role !== 'staff') {
      navigate('/staff-login');
      return;
    }

    setStaff(staffData);
  };

  const loadStaffData = async () => {
    setLoading(true);
    try {
      // Only fetch staff details if we don't already have complete data (check for profilePicture)
      let completeStaffData = staff;
      if (!staff.profilePicture || !staff.profilePicture.startsWith('http')) {
        // Load staff details from staff.json - this now includes profile picture
        const staffResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${staff.id}`);
        if (staffResponse.ok) {
          completeStaffData = await staffResponse.json();
          setStaff(completeStaffData);
        } else {
          console.warn('Failed to fetch staff details, using localStorage data');
        }
      }

      // Load staff attendance
      const attendanceResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${staff.id}/attendance`);
      if (attendanceResponse.ok) {
        const attendanceData = await attendanceResponse.json();
        setAttendanceRecords(attendanceData);
      }

      // Load staff salaries
      const salaryResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${staff.id}/salaries`);
      if (salaryResponse.ok) {
        const salaryData = await salaryResponse.json();
        setSalaryRecords(salaryData);
      }

      // Load staff feedback/requests
      const feedbackResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${staff.id}/feedback`);
      if (feedbackResponse.ok) {
        const feedbackData = await feedbackResponse.json();
        setFeedbackRecords(feedbackData);
      }

    } catch (error) {
      console.error('Error loading staff data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('staffUser');
    navigate('/staff-login');
  };

  const markAttendance = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${staff.id}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: new Date().toISOString().split('T')[0],
          status: 'present',
          notes: 'Marked via dashboard'
        })
      });

      if (response.ok) {
        alert('Attendance marked successfully!');
        loadStaffData(); // Refresh data
      } else {
        alert('Failed to mark attendance');
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Failed to mark attendance');
    }
  };

  const submitFeedback = async (feedbackData) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${staff.id}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackData)
      });

      if (response.ok) {
        alert('Feedback/request submitted successfully!');
        loadStaffData(); // Refresh data
      } else {
        alert('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback');
    }
  };

  const getAttendanceStats = () => {
    const present = attendanceRecords.filter(r => r.status === 'present').length;
    const total = attendanceRecords.length;
    const rate = total > 0 ? Math.round((present / total) * 100) : 0;
    return { present, total, rate };
  };

  const getPendingRequests = () => {
    return feedbackRecords.filter(r => r.status === 'pending').length;
  };

  const loadAdminCards = async () => {
    try {
      // Load available admin cards
      const availableResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/admin-cards/available`);
      if (availableResponse.ok) {
        const availableData = await availableResponse.json();
        setAvailableAdminCards(availableData.adminCards);
      }

      // Load assigned admin cards
      const assignedResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${staff.id}/admin-cards`);
      if (assignedResponse.ok) {
        const assignedData = await assignedResponse.json();
        setAssignedAdminCards(assignedData.assignedAdminCards);
      }
    } catch (error) {
      console.error('Error loading admin cards:', error);
    }
  };

  const handleAdminCardClick = (cardId) => {
    // Find the card details
    const card = availableAdminCards.find(c => c.id === cardId);
    if (card) {
      navigate(card.path);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading staff dashboard...</div>
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-red-600">Access denied. Please login as staff.</div>
      </div>
    );
  }

  const attendanceStats = getAttendanceStats();
  const pendingRequests = getPendingRequests();
  const latestSalary = salaryRecords[0];

  return (
    <div className="app-vibrant-shell p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="app-vibrant-header flex flex-col lg:flex-row justify-between lg:items-center gap-4 mb-8 p-5 md:p-6">
          <div className="flex flex-wrap items-center gap-3">
            {/* Profile Picture */}
            <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-sky-200 shadow-lg">
              <img
                src={staff.profilePicture || '/fallback-data/default-avatar.png'}
                alt={`${staff.firstName} ${staff.lastName}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = '/fallback-data/default-avatar.png';
                }}
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-950">Staff Dashboard</h1>
              <p className="text-gray-600 mt-1">
                Welcome back, {staff.firstName} {staff.lastName}
              </p>
              <p className="text-sm text-gray-500">
                Employee ID: {staff.employeeId} | Department: {staff.department}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <DateTimeDisplay />
            <button
              onClick={handleLogout}
              className="bg-rose-600 text-white px-4 py-2 rounded-lg hover:bg-rose-700 transition duration-300 shadow-md"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="app-vibrant-tabs flex mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`app-vibrant-tab px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'overview'
                ? 'app-vibrant-tab-active'
                : 'text-gray-600 hover:text-sky-700 hover:bg-sky-50'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('attendance')}
            className={`app-vibrant-tab px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'attendance'
                ? 'app-vibrant-tab-active'
                : 'text-gray-600 hover:text-sky-700 hover:bg-sky-50'
            }`}
          >
            My Attendance
          </button>
          <button
            onClick={() => setActiveTab('salary')}
            className={`app-vibrant-tab px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'salary'
                ? 'app-vibrant-tab-active'
                : 'text-gray-600 hover:text-sky-700 hover:bg-sky-50'
            }`}
          >
            Salary Info
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`app-vibrant-tab px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'requests'
                ? 'app-vibrant-tab-active'
                : 'text-gray-600 hover:text-sky-700 hover:bg-sky-50'
            }`}
          >
            Requests ({pendingRequests})
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`app-vibrant-tab px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'profile'
                ? 'app-vibrant-tab-active'
                : 'text-gray-600 hover:text-sky-700 hover:bg-sky-50'
            }`}
          >
            Profile
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`app-vibrant-tab px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap ${
              activeTab === 'manage'
                ? 'app-vibrant-tab-active'
                : 'text-gray-600 hover:text-sky-700 hover:bg-sky-50'
            }`}
          >
            Manage ({assignedAdminCards.length})
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#0ea5e9', '--card-soft': '#e0f2fe' }}>
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-full">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Attendance Rate</p>
                  <p className="text-2xl font-bold text-gray-900">{attendanceStats.rate}%</p>
                </div>
              </div>
            </div>

            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#10b981', '--card-soft': '#d1fae5' }}>
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-full">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Latest Salary</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ₦{latestSalary?.netSalary?.toLocaleString() || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#8b5cf6', '--card-soft': '#ede9fe' }}>
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-full">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Pending Requests</p>
                  <p className="text-2xl font-bold text-gray-900">{pendingRequests}</p>
                </div>
              </div>
            </div>

            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#f59e0b', '--card-soft': '#fef3c7' }}>
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Days Present</p>
                  <p className="text-2xl font-bold text-gray-900">{attendanceStats.present}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Attendance Tab */}
        {activeTab === 'attendance' && (
          <div className="space-y-6">
            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#0ea5e9', '--card-soft': '#e0f2fe' }}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">My Attendance</h3>
                <button
                  onClick={markAttendance}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                >
                  Mark Today's Attendance
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 font-medium">Total Days</p>
                  <p className="text-2xl font-bold text-blue-900">{attendanceStats.total}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-600 font-medium">Present Days</p>
                  <p className="text-2xl font-bold text-green-900">{attendanceStats.present}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-purple-600 font-medium">Attendance Rate</p>
                  <p className="text-2xl font-bold text-purple-900">{attendanceStats.rate}%</p>
                </div>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {attendanceRecords.slice(0, 30).map((record) => (
                  <div key={record.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-medium">{record.date}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        record.status === 'present' ? 'bg-green-100 text-green-800' :
                        record.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {record.status}
                      </span>
                      {record.checkInTime && (
                        <span className="text-sm text-gray-600">
                          {record.checkInTime}
                          {record.checkOutTime && ` - ${record.checkOutTime}`}
                        </span>
                      )}
                    </div>
                    {record.notes && (
                      <span className="text-sm text-gray-600 italic">"{record.notes}"</span>
                    )}
                  </div>
                ))}
              </div>

              {attendanceRecords.length === 0 && (
                <p className="text-gray-500 text-center py-8">No attendance records found.</p>
              )}
            </div>
          </div>
        )}

        {/* Salary Tab */}
        {activeTab === 'salary' && (
          <div className="space-y-6">
            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#10b981', '--card-soft': '#d1fae5' }}>
              <h3 className="text-lg font-semibold text-gray-800 mb-6">Salary Information</h3>

              {latestSalary ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded">
                      <p className="text-sm text-gray-600">Month</p>
                      <p className="text-lg font-bold">{latestSalary.month}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded">
                      <p className="text-sm text-blue-600">Net Salary</p>
                      <p className="text-xl font-bold text-blue-900">
                        ₦{latestSalary.netSalary?.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-800 mb-3">Salary Breakdown</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-green-50 rounded">
                        <p className="text-xs text-green-600">Base Salary</p>
                        <p className="font-bold">₦{latestSalary.baseSalary?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded">
                        <p className="text-xs text-blue-600">Allowances</p>
                        <p className="font-bold">₦{latestSalary.totalAllowances?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded">
                        <p className="text-xs text-red-600">Deductions</p>
                        <p className="font-bold">₦{latestSalary.totalDeductions?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded">
                        <p className="text-xs text-purple-600">Payment Status</p>
                        <p className={`font-bold ${
                          latestSalary.paymentStatus === 'paid' ? 'text-green-600' :
                          latestSalary.paymentStatus === 'pending' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {latestSalary.paymentStatus}
                        </p>
                      </div>
                    </div>
                  </div>

                  {latestSalary.paymentDate && (
                    <div className="mt-4 p-3 bg-green-50 rounded">
                      <p className="text-sm text-green-800">
                        <strong>Last Payment:</strong> {new Date(latestSalary.paymentDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No salary information available.</p>
                  <p className="text-sm text-gray-400 mt-2">Contact administration for salary details.</p>
                </div>
              )}
            </div>

            {salaryRecords.length > 1 && (
              <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#8b5cf6', '--card-soft': '#ede9fe' }}>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Salary History</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {salaryRecords.slice(1, 6).map((record) => (
                    <div key={record.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <span className="font-medium">{record.month}</span>
                      <div className="text-right">
                        <p className="font-bold">₦{record.netSalary?.toLocaleString()}</p>
                        <p className={`text-sm ${
                          record.paymentStatus === 'paid' ? 'text-green-600' : 'text-yellow-600'
                        }`}>
                          {record.paymentStatus}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="space-y-6">
            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#f59e0b', '--card-soft': '#fef3c7' }}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">My Requests & Feedback</h3>
                <button
                  onClick={() => setActiveTab('submit-request')}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                >
                  Submit New Request
                </button>
              </div>

              <div className="space-y-4">
                {feedbackRecords.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No requests or feedback submitted yet.</p>
                ) : (
                  feedbackRecords.map((record) => (
                    <div key={record.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-semibold text-gray-800">{record.title}</h4>
                            <span className={`px-2 py-1 rounded text-xs ${
                              record.type === 'performance_feedback' ? 'bg-blue-100 text-blue-800' :
                              record.type === 'leave_request' ? 'bg-green-100 text-green-800' :
                              record.type === 'equipment_request' ? 'bg-orange-100 text-orange-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {record.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              record.status === 'approved' ? 'bg-green-100 text-green-800' :
                              record.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {record.status.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-gray-600 mb-2">{record.message}</p>
                          <p className="text-sm text-gray-500">
                            Submitted: {new Date(record.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Request Details */}
                      {record.requestDetails && (
                        <div className="mt-3 p-3 bg-gray-50 rounded">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Request Details:</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            {record.requestDetails.startDate && (
                              <p><strong>Start Date:</strong> {record.requestDetails.startDate}</p>
                            )}
                            {record.requestDetails.endDate && (
                              <p><strong>End Date:</strong> {record.requestDetails.endDate}</p>
                            )}
                            {record.requestDetails.reason && (
                              <p><strong>Reason:</strong> {record.requestDetails.reason}</p>
                            )}
                            {record.requestDetails.item && (
                              <p><strong>Item:</strong> {record.requestDetails.item}</p>
                            )}
                            {record.requestDetails.justification && (
                              <p><strong>Justification:</strong> {record.requestDetails.justification}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Review Comments */}
                      {record.adminComments && (
                        <div className="mt-3 p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                          <p className="text-sm font-medium text-blue-700">Admin Response:</p>
                          <p className="text-sm text-blue-600">{record.adminComments}</p>
                          {record.reviewedAt && (
                            <p className="text-xs text-blue-500 mt-1">
                              Reviewed: {new Date(record.reviewedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Submit Request Tab */}
        {activeTab === 'submit-request' && (
          <RequestForm onSubmit={submitFeedback} onCancel={() => setActiveTab('requests')} />
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#0ea5e9', '--card-soft': '#e0f2fe' }}>
            <h3 className="text-lg font-semibold text-gray-800 mb-6">My Profile</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-medium text-gray-800">Personal Information</h4>
                <div className="space-y-2">
                  <p><strong>Full Name:</strong> {staff.firstName} {staff.lastName}</p>
                  <p><strong>Employee ID:</strong> {staff.employeeId}</p>
                  <p><strong>Email:</strong> {staff.email}</p>
                  <p><strong>Phone:</strong> {staff.phone}</p>
                  <p><strong>Department:</strong> {staff.department}</p>
                  <p><strong>Role:</strong> {staff.role}</p>
                  <p><strong>Employment Status:</strong> {staff.employmentStatus}</p>
                  <p><strong>Hire Date:</strong> {staff.hireDate ? new Date(staff.hireDate).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-gray-800">Emergency Contact</h4>
                {staff.emergencyContact?.name ? (
                  <div className="space-y-2">
                    <p><strong>Name:</strong> {staff.emergencyContact.name}</p>
                    <p><strong>Relationship:</strong> {staff.emergencyContact.relationship}</p>
                    <p><strong>Phone:</strong> {staff.emergencyContact.phone}</p>
                  </div>
                ) : (
                  <p className="text-gray-500">No emergency contact information provided.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Manage Tab */}
        {activeTab === 'manage' && (
          <div className="space-y-6">
            <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#10b981', '--card-soft': '#d1fae5' }}>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Assigned Admin Cards</h3>
              <p className="text-gray-600 mb-6">
                These are the administrative functions you have been assigned to manage. Click on any card to access the corresponding feature.
              </p>

              {assignedAdminCards.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {assignedAdminCards.map((cardId) => {
                    const card = availableAdminCards.find(c => c.id === cardId);
                    if (!card) return null;

                    return (
                      <div
                        key={cardId}
                        onClick={() => handleAdminCardClick(cardId)}
                        className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl shadow-lg p-6 hover:shadow-xl cursor-pointer transition duration-300 border border-blue-200 hover:border-blue-300"
                      >
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="text-3xl">{card.icon}</div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-800">{card.title}</h4>
                            <p className="text-sm text-gray-600">{card.description}</p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            card.category === 'academic' ? 'bg-blue-100 text-blue-800' :
                            card.category === 'finance' ? 'bg-green-100 text-green-800' :
                            card.category === 'hr' ? 'bg-purple-100 text-purple-800' :
                            card.category === 'communication' ? 'bg-orange-100 text-orange-800' :
                            card.category === 'monitoring' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {card.category.toUpperCase()}
                          </span>
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Admin Cards Assigned</h3>
                  <p className="text-gray-500">
                    You haven't been assigned any administrative functions yet. Contact your administrator to request access to specific management features.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Request Form Component
const RequestForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    type: 'performance_feedback',
    title: '',
    message: '',
    requestDetails: {
      startDate: '',
      endDate: '',
      reason: '',
      item: '',
      justification: ''
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRequestDetailChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      requestDetails: {
        ...prev.requestDetails,
        [field]: value
      }
    }));
  };

  return (
    <div className="app-vibrant-card rounded-2xl p-6" style={{ '--card-color': '#8b5cf6', '--card-soft': '#ede9fe' }}>
      <h3 className="text-lg font-semibold text-gray-800 mb-6">Submit Request or Feedback</h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
          <select
            value={formData.type}
            onChange={(e) => handleChange('type', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="performance_feedback">Performance Feedback</option>
            <option value="leave_request">Leave Request</option>
            <option value="equipment_request">Equipment Request</option>
            <option value="maintenance_request">Maintenance Request</option>
            <option value="other_request">Other Request</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Brief title for your request/feedback"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
          <textarea
            value={formData.message}
            onChange={(e) => handleChange('message', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="4"
            placeholder="Detailed description of your request or feedback"
            required
          />
        </div>

        {/* Conditional fields based on request type */}
        {(formData.type === 'leave_request') && (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-gray-800">Leave Request Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={formData.requestDetails.startDate}
                  onChange={(e) => handleRequestDetailChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={formData.requestDetails.endDate}
                  onChange={(e) => handleRequestDetailChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <select
                value={formData.requestDetails.reason}
                onChange={(e) => handleRequestDetailChange('reason', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select reason</option>
                <option value="vacation">Vacation</option>
                <option value="sick_leave">Sick Leave</option>
                <option value="personal">Personal</option>
                <option value="maternity">Maternity</option>
                <option value="bereavement">Bereavement</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        )}

        {(formData.type === 'equipment_request') && (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-gray-800">Equipment Request Details</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Item Requested</label>
              <input
                type="text"
                value={formData.requestDetails.item}
                onChange={(e) => handleRequestDetailChange('item', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="What equipment do you need?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Justification</label>
              <textarea
                value={formData.requestDetails.justification}
                onChange={(e) => handleRequestDetailChange('justification', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Why do you need this equipment?"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            Submit Request
          </button>
        </div>
      </form>
    </div>
  );
};

export default StaffDashboard;
